import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {
  useDeleteGuestMutation,
  useUpdateGuestMutation,
} from '~/ViewInvitation/api/endpoints/guests';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import type {UpdateBaseQueryArgs} from '~/ViewInvitation/types/api';

/**
 * `userId` here is the guest to perform actions on, not the current user
 * @param userId string
 */
export const useGuestApi = (userId: string) => {
  const eventId = useSelector(selectEventId);
  const [deleteGuest, deleteGuestStatus] = useDeleteGuestMutation({
    fixedCacheKey: 'shared-remove-guest-key',
  });
  const [updateGuest, updateGuestStatus] = useUpdateGuestMutation({
    fixedCacheKey: 'shared-update-guest-key',
  });

  const deleteGuestFn = useCallback(async () => {
    return deleteGuest({eventId, userId}).unwrap();
  }, [deleteGuest, eventId, userId]);

  const updateGuestFn = useCallback(
    async (guest: UpdateBaseQueryArgs['guest']) => {
      return updateGuest({userId, eventId, guest}).unwrap();
    },
    [updateGuest, eventId, userId]
  );

  const isLoading = deleteGuestStatus.isLoading || updateGuestStatus.isLoading;
  const isError = deleteGuestStatus.isError || updateGuestStatus.isError;
  const error = deleteGuestStatus.error || updateGuestStatus.error;

  return {
    deleteGuest: deleteGuestFn,
    updateGuest: updateGuestFn,
    isDeleting: deleteGuestStatus.isLoading,
    isUpdating: updateGuestStatus.isLoading,
    isLoading,
    isError,
    error,
    status: {
      deleteGuest: deleteGuestStatus,
      updateGuest: updateGuestStatus,
    },
  };
};
